import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
  Alert,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import MissingSelectOption from '../components/MissingSelectOption';
import { createTooltipProps } from '../utils/fieldTooltips';

function SalesCategoryModal({
  salesCategory,
  salesNominals,
  purchaseNominals,
  onClose,
  onSave,
  error,
}) {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    tax_code: '',
    nc_sales: '',
    nc_purchase: '',
    individual: false,
  });

  useEffect(() => {
    if (salesCategory) {
      setFormData(salesCategory);
    }
  }, [salesCategory]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {salesCategory ? 'Edit Sales Category' : 'Add New Sales Category'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Code *</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('code')}>
                      Used in equipment classification, pricing, and sales contract management
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    maxLength="20"
                    required
                    {...createTooltipProps('code')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('name')}>
                      Used in user interfaces, reports, and equipment selection
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    maxLength="100"
                    required
                    {...createTooltipProps('name')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Tax Code</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('tax_code')}>
                      Tax code for sales transactions
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="text"
                    name="tax_code"
                    value={formData.tax_code}
                    onChange={handleChange}
                    maxLength="10"
                    {...createTooltipProps('tax_code')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>N/C Sales</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('nc_sales')}>
                      Nominal code for sales transactions
                    </Tooltip>
                  }
                >
                  <Form.Select
                    name="nc_sales"
                    value={formData.nc_sales || ''}
                    onChange={handleChange}
                    {...createTooltipProps('nc_sales')}
                  >
                    <option value="">Select nominal...</option>
                    <MissingSelectOption
                      value={formData.nc_sales}
                      options={salesNominals}
                      getOptionValue={(nc) => nc.code}
                    />
                    {salesNominals.map((nc) => (
                      <option key={nc.id} value={nc.code}>
                        {nc.code} - {nc.name}
                      </option>
                    ))}
                  </Form.Select>
                </OverlayTrigger>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>N/C Purchase</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('nc_purchase')}>
                      Nominal code for purchase transactions
                    </Tooltip>
                  }
                >
                  <Form.Select
                    name="nc_purchase"
                    value={formData.nc_purchase || ''}
                    onChange={handleChange}
                    {...createTooltipProps('nc_purchase')}
                  >
                    <option value="">Select nominal...</option>
                    <MissingSelectOption
                      value={formData.nc_purchase}
                      options={purchaseNominals}
                      getOptionValue={(nc) => nc.code}
                    />
                    {purchaseNominals.map((nc) => (
                      <option key={nc.id} value={nc.code}>
                        {nc.code} - {nc.name}
                      </option>
                    ))}
                  </Form.Select>
                </OverlayTrigger>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip {...createTooltipProps('individual')}>
                  Indicates if this category is for individual items
                </Tooltip>
              }
            >
              <Form.Check
                type="checkbox"
                name="individual"
                label="Individual Item Tracking"
                checked={formData.individual}
                onChange={handleChange}
                {...createTooltipProps('individual')}
              />
            </OverlayTrigger>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {salesCategory ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function SalesCategories() {
  const [salesCategories, setSalesCategories] = useState([]);
  const [salesNominals, setSalesNominals] = useState([]);
  const [purchaseNominals, setPurchaseNominals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSalesCategory, setEditingSalesCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalError, setModalError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchSalesCategories = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/sales-categories', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });
      setSalesCategories(response.data.salesCategories);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch sales categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchNominalCodes = async () => {
    try {
      const [salesRes, purchaseRes] = await Promise.all([
        axios.get('/api/nominal-codes', { params: { type: 'S', limit: 500 } }),
        axios.get('/api/nominal-codes', { params: { type: 'P', limit: 500 } }),
      ]);
      setSalesNominals(salesRes.data.nominalCodes || []);
      setPurchaseNominals(purchaseRes.data.nominalCodes || []);
    } catch (error) {
      console.error('Failed to fetch nominal codes:', error);
    }
  };

  useEffect(() => {
    fetchSalesCategories();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSalesCategories(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchSalesCategories(newPage, searchTerm);
  };

  const handleAddSalesCategory = () => {
    setEditingSalesCategory(null);
    setModalError('');
    fetchNominalCodes();
    setShowModal(true);
  };

  const handleEditSalesCategory = (salesCategory) => {
    setEditingSalesCategory(salesCategory);
    setModalError('');
    fetchNominalCodes();
    setShowModal(true);
  };

  const handleSaveSalesCategory = async (salesCategoryData) => {
    try {
      if (editingSalesCategory) {
        const response = await axios.put(
          `/api/sales-categories/${editingSalesCategory.id}`,
          salesCategoryData
        );
        let successMessage = 'Sales category updated successfully';

        // Check if cascade updates occurred
        if (response.data.cascadeUpdates) {
          const { salesItems, inclusions } = response.data.cascadeUpdates;
          const totalUpdates = (salesItems || 0) + (inclusions || 0);

          if (totalUpdates > 0) {
            const parts = [];
            if (salesItems > 0)
              parts.push(`${salesItems} sales item${salesItems !== 1 ? 's' : ''}`);
            if (inclusions > 0) parts.push(`${inclusions} inclusion${inclusions !== 1 ? 's' : ''}`);
            successMessage += `. Also updated ${parts.join(' and ')} with the new category code.`;
          }
        }

        setSuccess(successMessage);
      } else {
        await axios.post('/api/sales-categories', salesCategoryData);
        setSuccess('Sales category created successfully');
      }
      setShowModal(false);
      fetchSalesCategories(pagination.page, searchTerm);
    } catch (error) {
      setModalError(error.response?.data?.error || 'Failed to save sales category');
    }
  };

  const handleDeleteSalesCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this sales category?')) {
      try {
        await axios.delete(`/api/sales-categories/${id}`);
        setSuccess('Sales category deleted successfully');
        fetchSalesCategories(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete sales category');
      }
    }
  };

  const handleDeleteAllSalesCategories = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL sales categories? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your sales category records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/sales-categories');
          setSuccess(
            `All sales categories deleted successfully (${response.data.deletedCount} records)`
          );
          fetchSalesCategories(1, '');
        } catch (error) {
          setError('Failed to delete all sales categories');
        }
      }
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i
              className="fas fa-shopping-cart me-3 text-warning"
              style={{ fontSize: '2.5rem' }}
            ></i>
            <div>
              <h1 className="mb-0">Sales Categories</h1>
              <p className="text-muted mb-0">Organize products and services for sale</p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button variant="primary" onClick={handleAddSalesCategory} className="me-2">
            Add Sales Category
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllSalesCategories}
            disabled={salesCategories.length === 0}
          >
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by code or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center p-3">Loading sales categories...</div>
          ) : salesCategories.length === 0 ? (
            <div className="text-center p-3">No sales categories found.</div>
          ) : (
            <>
              <Table striped bordered hover responsive className="mb-3" style={{ marginLeft: 0 }}>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Tax Code</th>
                    <th>N/C Sales</th>
                    <th>N/C Purchase</th>
                    <th>Individual</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {salesCategories.map((salesCategory) => (
                    <tr key={salesCategory.id}>
                      <td>
                        <strong>{salesCategory.code}</strong>
                      </td>
                      <td>{salesCategory.name}</td>
                      <td>{salesCategory.tax_code}</td>
                      <td>{salesCategory.nc_sales}</td>
                      <td>{salesCategory.nc_purchase}</td>
                      <td>
                        <Badge bg={salesCategory.individual ? 'success' : 'secondary'}>
                          {salesCategory.individual ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditSalesCategory(salesCategory)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteSalesCategory(salesCategory.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <SalesCategoryModal
          salesCategory={editingSalesCategory}
          salesNominals={salesNominals}
          purchaseNominals={purchaseNominals}
          onClose={() => {
            setShowModal(false);
            setModalError('');
          }}
          onSave={handleSaveSalesCategory}
          error={modalError}
        />
      )}
    </Container>
  );
}

export default SalesCategories;
